import { RuleTableResponse } from "types/table";
import { IdNamePair, TableSummary } from "types/tableSummary";
import { TableSummariesState } from "./tables.types";
import { ObjectAssignedEnum } from "types/validation";
import type { Validation } from "types/validation";
import {
  countValidationsBySeverity,
  totalValidationIssueCount,
  ValidationSeverityCounts,
} from "../validation/validationCounts.helpers";

export type { ValidationSeverityCounts };
export { totalValidationIssueCount };

function toIdNamePair(item: { id?: string; name: string }): IdNamePair {
  return { id: item.id ?? "", name: item.name };
}

/**
 * Redux always initializes summaries, but helpers defensively no-op when state
 * or data is missing (e.g. tests or partial state).
 */
function guardSummaries(
  summaries: TableSummariesState | null | undefined
): TableSummariesState | null {
  if (summaries == null || summaries.data == null) {
    return null;
  }
  return summaries;
}

/** Count RULETABLE validations for a table, broken out by severity. */
export function countRuleTableValidationsBySeverity(
  validations: Validation[] | null | undefined,
  tableId: string
): ValidationSeverityCounts {
  return countValidationsBySeverity(
    validations,
    (v) =>
      v.objectAssigned === ObjectAssignedEnum.RULETABLE &&
      v.objectId === tableId
  );
}

export function tableSummaryFromResponse(
  table: RuleTableResponse
): TableSummary {
  const lookupCount = table.lookUpFieldIndex ?? 0;
  const validationCounts = countRuleTableValidationsBySeverity(
    table.validations,
    table.id
  );

  return {
    tableId: table.id,
    compareId: table.compareId,
    tableType: table.tableType,
    lookupProperties: table.properties.slice(0, lookupCount).map(toIdNamePair),
    dependencyProperties: table.properties.slice(lookupCount).map(toIdNamePair),
    rulesets: [...table.rulesets]
      .map(toIdNamePair)
      .sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { sensitivity: "base" })
      ),
    ...validationCounts,
    rowCount: table.rows?.length ?? 0,
    comments: table.comments,
    sources: table.sources,
    ownerTool: table.ownerTool,
  };
}

/**
 * Patch validation severity counts on an existing summary row without requiring
 * the full rule table to be loaded in byId (e.g. Validate from the summary grid).
 */
export function updateTableSummaryValidationCount(
  summaries: TableSummariesState,
  tableId: string,
  counts: ValidationSeverityCounts
): TableSummariesState {
  const state = guardSummaries(summaries);
  if (!state) return summaries;

  const index = state.data.findIndex((s) => s.tableId === tableId);
  if (index === -1) return summaries;

  const data = [...state.data];
  data[index] = { ...data[index], ...counts };

  return { ...state, data };
}

/**
 * Only append when the summary grid has been loaded at least once.
 * Avoids populating summaries before the user visits the summary view.
 */
export function addTableSummaryIfLoaded(
  summaries: TableSummariesState,
  table: RuleTableResponse
): TableSummariesState {
  const state = guardSummaries(summaries);
  if (!state || state.status !== "loaded") {
    return summaries;
  }

  if (state.data.some((s) => s.tableId === table.id)) {
    return summaries;
  }

  return {
    ...state,
    data: [...state.data, tableSummaryFromResponse(table)],
  };
}

/**
 * Remove by tableId whenever that row exists in data — not only when status is
 * "loaded". During "loading" we keep prior data visible; a delete should update
 * the grid immediately. When status is "idle", data is empty and this is a no-op.
 */
export function removeTableSummary(
  summaries: TableSummariesState,
  tableId: string
): TableSummariesState {
  const state = guardSummaries(summaries);
  if (!state || !state.data.some((s) => s.tableId === tableId)) {
    return summaries;
  }

  return {
    ...state,
    data: state.data.filter((s) => s.tableId !== tableId),
  };
}

/**
 * Patch an existing row when present in data. Same rationale as remove: apply
 * while refetching ("loading") or showing stale data after "error".
 */
export function syncTableSummaryIfPresent(
  summaries: TableSummariesState,
  table: RuleTableResponse
): TableSummariesState {
  const state = guardSummaries(summaries);
  if (!state) {
    return summaries;
  }

  const index = state.data.findIndex((s) => s.tableId === table.id);
  if (index === -1) {
    return summaries;
  }

  const data = [...state.data];
  data[index] = tableSummaryFromResponse(table);

  return { ...state, data };
}
